import { useEffect, useRef, useState } from "react"
import type { AuthStatus } from "../../types"
import {
    getLocalStream,
    getRemoteStream,
    setMicEnabled,
} from "../../lib/webrtc"
import { useLinkStore } from "../../stores/useLinkStore"
import { useNetworkStore } from "../../stores/useNetworkStore"
import { usePlayerStore } from "../../stores/usePlayerStore"
import {
    useRendererStore,
    type RendererMode,
} from "../../stores/useRendererStore"
import { useIsMobile } from "../../hooks/useDevice"

interface HUDProps {
    authStatus: AuthStatus
    connected: boolean
    roomId: string | null
    selfName?: string | null
    peerCount: number
    error?: string | null
}

// HUD del Domo Cero. Una sola tarjeta de identidad arriba a la izquierda
// con dot de estado + EN LÍNEA + nombre del Tripulante. Si hay enlace
// activo, el banner aparece debajo del panel (no choca con la identidad).
// Sin sello bottom-center, sin pista de gesto, sin línea de cuásar/peers
// (el Tripulante ya sabe dónde está y todo lo accesorio quita altura
// preciosa al lienzo 3D). Mic toggle aparece SOLO durante sealed.
//
// noSelect: bloquea selección de texto durante long-press en iOS
// (Safari arma "selección" automática que pinta el panel completo en
// azul translúcido). Se aplica en cada panel del HUD para que el bug
// no aparezca en ningún elemento, no solo en el body.
const noSelect = {
    userSelect: "none" as const,
    WebkitUserSelect: "none" as const,
    WebkitTouchCallout: "none" as const,
    WebkitTapHighlightColor: "transparent",
}

export function HUD({ error }: HUDProps) {
    const isMobile = useIsMobile()

    const phase = useLinkStore((s) => s.phase)
    const peerId = useLinkStore((s) => s.peerId)
    const myReachTarget = useLinkStore((s) => s.myReachTarget)
    const peers = useNetworkStore((s) => s.peers)
    const peer = peerId ? peers.get(peerId) : null
    const reachPeer = myReachTarget ? peers.get(myReachTarget) : null
    const isAnchored = usePlayerStore((s) => s.isAnchored)
    const rendererMode = useRendererStore((s) => s.mode)

    /* Padding del wrapper para que los pills queden alineados con el
       borde del viewport. */
    const sidePad = isMobile ? 10 : 16
    const topPad = isMobile ? 10 : 16
    /* Offset vertical que dejan libre los elementos top-left para que
       no se monten unos sobre otros. El indicador de motor vive en
       (topPad, sidePad); el resto baja a (topPad + dotOffset, …). */
    const dotOffset = 22

    return (
        <div
            className="absolute inset-0 pointer-events-none font-mono"
            style={{
                /* Honoreamos los safe-area-inset para que ningún panel
                   choque con la barra de notch o la home bar de iPhone. */
                paddingTop: "env(safe-area-inset-top, 0px)",
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
                paddingLeft: "env(safe-area-inset-left, 0px)",
                paddingRight: "env(safe-area-inset-right, 0px)",
                ...noSelect,
            }}
        >
            {/* 2026-05-06 — Indicador de motor de render. Punto verde
                pulsando si el Canvas está sobre WebGPU, azul pulsando
                si está sobre WebGL clásico. Sólo desaparece durante
                el "unknown" inicial (entre mount del Canvas y
                resolución del gl factory async). */}
            <RendererIndicator
                mode={rendererMode}
                topPad={topPad}
                sidePad={sidePad}
            />

            {/* Anclado pill — sólo cuando el Tripulante está anclado.
                Vive bajo el indicador de motor. */}
            {isAnchored && (
                <div
                    className="absolute pointer-events-auto"
                    style={{
                        top: topPad + dotOffset,
                        left: sidePad,
                        ...noSelect,
                    }}
                >
                    <div
                        className="rounded-full backdrop-blur-md bg-black/55 border uppercase"
                        style={{
                            padding: isMobile ? "5px 12px" : "6px 14px",
                            fontSize: isMobile ? 9.5 : 10.5,
                            letterSpacing: isMobile ? "0.22em" : "0.3em",
                            color: "#D4AF37",
                            borderColor: "rgba(212,175,55,0.45)",
                            textShadow: "0 0 8px rgba(212,175,55,0.55)",
                            display: "inline-block",
                            ...noSelect,
                        }}
                    >
                        ✦ Anclado · intocable
                    </div>
                </div>
            )}

            {/* Error / desconexión — visible sólo cuando algo falla. */}
            {error && (
                <div
                    className="absolute pointer-events-auto"
                    style={{
                        top: topPad + dotOffset,
                        left: sidePad,
                        right: isMobile ? sidePad : undefined,
                        maxWidth: isMobile ? undefined : 480,
                        ...noSelect,
                    }}
                >
                    <div
                        className="rounded-md backdrop-blur-md bg-black/55 border"
                        style={{
                            padding: "6px 10px",
                            color: "#FF6B7A",
                            borderColor: "rgba(255,107,122,0.4)",
                            fontSize: isMobile ? 10 : 11.5,
                            ...noSelect,
                        }}
                    >
                        {error}
                    </div>
                </div>
            )}

            {/* Banner del Enlace Geométrico — debajo del indicador de
                motor. Sigue siendo overlay discreto sobre el campo. */}
            <LinkBanner
                phase={phase}
                peerName={peer?.displayName}
                reachName={reachPeer?.displayName}
                isMobile={isMobile}
                topOffset={topPad + dotOffset}
                sideOffset={sidePad}
            />

            {/* Meters de audio — visibles sólo durante sealed.
                · TÚ: barra del mic local. Si esto se mueve cuando
                  hablas, tu mic está capturando bien.
                · PEER: barra del audio remoto. Si esto se mueve
                  cuando el otro habla, el audio está llegando.
                Diagnóstico: TÚ vacío → tu mic no captura. PEER vacío
                con TÚ activo → tu transmisión llega pero la del peer
                no, o la negociación SDP/ICE falló del lado peer→tú. */}
            {phase === "sealed" && (
                <AudioMeters isMobile={isMobile} />
            )}

            {/* Toggle de micro — solo durante sealed. Único elemento
                bottom permitido; el resto del fondo queda libre para
                el campo 3D. */}
            {phase === "sealed" && <MicToggle isMobile={isMobile} />}

            {/* 2026-05-05 v6 — Toggle de primera persona. Sólo
                aparece dentro del Domo activo (no en void/descenso),
                en bottom-left para no chocar con el toggle de mic
                en bottom-right. */}
            <CameraModeToggle isMobile={isMobile} />
        </div>
    )
}

function CameraModeToggle({ isMobile }: { isMobile?: boolean }) {
    const phase = usePlayerStore((s) => s.phase)
    const cameraMode = usePlayerStore((s) => s.cameraMode)
    const toggleCameraMode = usePlayerStore((s) => s.toggleCameraMode)
    if (phase !== "incarnated") return null
    const isFP = cameraMode === "firstPerson"
    const accent = isFP ? "#FFD27A" : "#7DEFFF"
    const accentRgb = isFP ? "212,175,55" : "0,229,255"
    if (isMobile) {
        return (
            <div
                className="absolute pointer-events-auto"
                style={{ bottom: 12, left: 12, ...noSelect }}
            >
                <button
                    onClick={toggleCameraMode}
                    aria-label={
                        isFP
                            ? "Cambiar a tercera persona"
                            : "Cambiar a primera persona"
                    }
                    className="rounded-full backdrop-blur-md bg-black/55 border transition-colors flex items-center justify-center"
                    style={{
                        width: 48,
                        height: 48,
                        borderColor: `rgba(${accentRgb},0.55)`,
                        color: accent,
                        textShadow: `0 0 8px rgba(${accentRgb},0.6)`,
                        fontSize: 18,
                        ...noSelect,
                    }}
                >
                    {isFP ? "👁" : "📷"}
                </button>
            </div>
        )
    }
    return (
        <div
            className="absolute bottom-6 left-6 pointer-events-auto"
            style={noSelect}
        >
            <button
                onClick={toggleCameraMode}
                className="px-4 py-2 rounded-full backdrop-blur-md bg-black/55 border text-[11px] uppercase tracking-[0.3em] transition-colors"
                style={{
                    borderColor: `rgba(${accentRgb},0.5)`,
                    color: accent,
                    textShadow: `0 0 8px rgba(${accentRgb},0.6)`,
                    ...noSelect,
                }}
            >
                {isFP ? "👁 1ª persona" : "📷 3ª persona"}
            </button>
        </div>
    )
}

function LinkBanner({
    phase,
    peerName,
    reachName,
    isMobile,
    topOffset,
    sideOffset,
}: {
    phase: string
    peerName?: string
    reachName?: string
    isMobile?: boolean
    topOffset: number
    sideOffset: number
}) {
    if (phase === "idle") return null
    let label: string
    let color: string
    if (phase === "reaching") {
        // 2026-05-05 — Copy más corto para que el banner sea compacto.
        // Antes: "Solicitud activa hacia X · espera respuesta · camina
        // para cancelar" — desbordaba en desktop como un recuadro negro
        // gigante. Ahora: una sola línea breve. El detalle "camina para
        // cancelar" lo manejan en el HUD del Domo si hace falta.
        label = `Solicitando enlace · ${reachName ?? "tripulante"}`
        color = "#7DEFFF"
    } else if (phase === "sealing") {
        label = "Sellando enlace…"
        color = "#D4AF37"
    } else {
        label = `En enlace con ${peerName ?? "tripulante"}`
        color = "#00E5FF"
    }
    return (
        <div
            className="absolute pointer-events-auto"
            style={{
                top: topOffset,
                left: sideOffset,
                /* Mobile: hasta el borde derecho. Desktop: ancho fijo
                   acotado (480px) para que el banner no se transforme en
                   un recuadro gigante en pantallas grandes. */
                right: isMobile ? sideOffset : undefined,
                maxWidth: isMobile ? undefined : 480,
                ...noSelect,
            }}
        >
            <div
                className="rounded-md backdrop-blur-md bg-black/45 border uppercase"
                style={{
                    padding: isMobile ? "7px 12px" : "8px 14px",
                    fontSize: isMobile ? 9.5 : 11,
                    letterSpacing: isMobile ? "0.16em" : "0.2em",
                    borderColor: `${color}66`,
                    color,
                    textShadow: `0 0 8px ${color}aa`,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.35,
                    ...noSelect,
                }}
            >
                ◈ {label}
            </div>
        </div>
    )
}

function MicToggle({ isMobile }: { isMobile?: boolean }) {
    const [enabled, setEnabled] = useState(true)
    useEffect(() => {
        setMicEnabled(enabled)
    }, [enabled])
    /* Mobile: botón circular compacto en bottom-right; el ícono
       comunica solo y deja el espacio para gestos de cámara. Desktop
       conserva el formato pill con texto. */
    if (isMobile) {
        return (
            <div
                className="absolute pointer-events-auto"
                style={{ bottom: 12, right: 12, ...noSelect }}
            >
                <button
                    onClick={() => setEnabled((v) => !v)}
                    aria-label={
                        enabled ? "Mutear micrófono" : "Activar micrófono"
                    }
                    className="rounded-full backdrop-blur-md bg-black/55 border transition-colors flex items-center justify-center"
                    style={{
                        width: 48,
                        height: 48,
                        borderColor: enabled
                            ? "rgba(0,229,255,0.55)"
                            : "rgba(255,61,85,0.55)",
                        color: enabled ? "#7DEFFF" : "#FF6B7A",
                        textShadow: enabled
                            ? "0 0 8px rgba(0,229,255,0.6)"
                            : "0 0 8px rgba(255,61,85,0.5)",
                        fontSize: 20,
                        ...noSelect,
                    }}
                >
                    {enabled ? "🎙" : "🔇"}
                </button>
            </div>
        )
    }
    return (
        <div
            className="absolute bottom-6 right-6 pointer-events-auto"
            style={noSelect}
        >
            <button
                onClick={() => setEnabled((v) => !v)}
                className="px-4 py-2 rounded-full backdrop-blur-md bg-black/55 border text-[11px] uppercase tracking-[0.3em] transition-colors"
                style={{
                    borderColor: enabled
                        ? "rgba(0,229,255,0.5)"
                        : "rgba(255,61,85,0.5)",
                    color: enabled ? "#7DEFFF" : "#FF6B7A",
                    textShadow: enabled
                        ? "0 0 8px rgba(0,229,255,0.6)"
                        : "0 0 8px rgba(255,61,85,0.5)",
                    ...noSelect,
                }}
            >
                {enabled ? "🎙 Micrófono activo" : "🔇 Silenciado"}
            </button>
        </div>
    )
}

// 2026-05-06 — Punto pulsante que comunica el motor del Canvas.
// Verde brillante = WebGPU. Azul brillante = WebGL clásico. Si todavía
// no resolvió el `gl` factory, el indicador se oculta para no asustar
// con un "?" intermedio. La pulsación viene del `pulse-slow` de Tailwind
// (1.6s) — sutil, no compite con el resto de la UI.
function RendererIndicator({
    mode,
    topPad,
    sidePad,
}: {
    mode: RendererMode
    topPad: number
    sidePad: number
}) {
    if (mode === "unknown") return null
    const isGpu = mode === "webgpu"
    const color = isGpu ? "#22E08C" : "#3DA8FF"
    const label = isGpu ? "WebGPU" : "WebGL"
    return (
        <div
            className="absolute pointer-events-none"
            style={{ top: topPad, left: sidePad, ...noSelect }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 9.5,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.62)",
                    textShadow: "0 0 6px rgba(0,0,0,0.8)",
                }}
            >
                <span
                    className="inline-block rounded-full animate-pulse-slow"
                    style={{
                        width: 9,
                        height: 9,
                        background: color,
                        boxShadow: `0 0 10px ${color}, 0 0 18px ${color}88`,
                    }}
                />
                <span>{label}</span>
            </div>
        </div>
    )
}

// 2026-05-07 — Diagnóstico dual de audio durante el enlace sellado.
//
// Dos columnas de barras en el HUD:
//   · TÚ   — nivel del mic local (lo que tu cuerpo está mandando).
//   · PEER — nivel del audio que llega del otro Tripulante.
//
// Si TÚ se mueve y PEER no: el otro lado no transmite o tu cliente
// no recibe el track remoto (chequeá en consola los logs `[domo]
// remote audio` / `WebRTC state`).
// Si TÚ no se mueve aunque hables: tu mic no captura — revisá permisos
// del navegador o si otra app tiene el micrófono tomado.
// Si ambos están planos: probable falla del intercambio SDP/ICE
// (revisar consola).
//
// Implementación: un único AudioContext compartido entre los dos
// meters para evitar duplicar pipeline. Cada stream tiene su propio
// AnalyserNode tap.
//
// 2026-05-07 — Workaround del bug iOS Safari "MediaStreamAudioSource
// silencioso cuando el stream también está conectado a un <audio>".
// Para el meter remoto CLONAMOS los tracks y construimos un nuevo
// MediaStream — la AudioContext lee de la copia clonada, mientras
// el <audio> sigue reproduciendo el stream original. Sin esto, en
// iOS la AnalyserNode reportaría 0 aunque el audio se escuche.

function useAudioMeter(
    streamGetter: () => MediaStream | null,
    enabled: boolean,
    cloneStream: boolean
): number {
    const [level, setLevel] = useState(0)
    const audioCtxRef = useRef<AudioContext | null>(null)
    const analyserRef = useRef<AnalyserNode | null>(null)
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
    const clonedStreamRef = useRef<MediaStream | null>(null)
    const rafRef = useRef<number | null>(null)
    const dataRef = useRef<Uint8Array | null>(null)

    useEffect(() => {
        if (!enabled) return
        let mounted = true
        const ctor =
            (window as any).AudioContext ||
            (window as any).webkitAudioContext
        if (!ctor) return

        const trySetup = () => {
            if (!mounted) return
            const stream = streamGetter()
            if (!stream || stream.getAudioTracks().length === 0) {
                rafRef.current = requestAnimationFrame(trySetup)
                return
            }
            try {
                const ctx: AudioContext = new ctor()
                if (ctx.state === "suspended") {
                    ctx.resume().catch(() => {})
                }
                // Si pedimos clonar, armamos un nuevo MediaStream con
                // copias de los tracks originales — workaround iOS.
                let analysisStream: MediaStream = stream
                if (cloneStream) {
                    const clones = stream
                        .getAudioTracks()
                        .map((t) => t.clone())
                    analysisStream = new MediaStream(clones)
                    clonedStreamRef.current = analysisStream
                }
                const source = ctx.createMediaStreamSource(analysisStream)
                const analyser = ctx.createAnalyser()
                analyser.fftSize = 256
                analyser.smoothingTimeConstant = 0.65
                source.connect(analyser)
                audioCtxRef.current = ctx
                analyserRef.current = analyser
                sourceRef.current = source
                dataRef.current = new Uint8Array(
                    analyser.frequencyBinCount
                )
                tick()
            } catch (err) {
                console.warn("[domo] audio meter setup falló:", err)
            }
        }

        const tick = () => {
            if (!mounted) return
            const analyser = analyserRef.current
            const data = dataRef.current
            if (analyser && data) {
                analyser.getByteFrequencyData(
                    data as Uint8Array<ArrayBuffer>
                )
                let sum = 0
                for (let i = 0; i < data.length; i++) sum += data[i]
                const avg = sum / data.length
                const next = Math.max(
                    0,
                    Math.min(1, (avg / 128) * 1.4)
                )
                setLevel((prev) => prev * 0.6 + next * 0.4)
            }
            rafRef.current = requestAnimationFrame(tick)
        }

        trySetup()

        return () => {
            mounted = false
            if (rafRef.current != null)
                cancelAnimationFrame(rafRef.current)
            try {
                sourceRef.current?.disconnect()
                analyserRef.current?.disconnect()
                audioCtxRef.current?.close()
                clonedStreamRef.current
                    ?.getTracks()
                    .forEach((t) => t.stop())
            } catch {}
            audioCtxRef.current = null
            analyserRef.current = null
            sourceRef.current = null
            clonedStreamRef.current = null
            dataRef.current = null
        }
    }, [enabled, streamGetter, cloneStream])

    return level
}

function AudioMeters({ isMobile }: { isMobile?: boolean }) {
    // El meter local NO clona el stream porque el local no se
    // reproduce vía <audio> (sólo se manda al PeerConnection). El
    // remoto SÍ clona — workaround iOS.
    const localLevel = useAudioMeter(getLocalStream, true, false)
    const remoteLevel = useAudioMeter(getRemoteStream, true, true)

    const bottom = isMobile ? 70 : 80
    const right = isMobile ? 68 : 100
    return (
        <div
            className="absolute pointer-events-none"
            style={{
                bottom,
                right,
                display: "flex",
                alignItems: "flex-end",
                gap: 14,
                ...noSelect,
            }}
        >
            <MeterColumn label="TÚ" level={localLevel} accent="#7DEFFF" />
            <MeterColumn
                label="PEER"
                level={remoteLevel}
                accent="#FFD27A"
            />
        </div>
    )
}

function MeterColumn({
    label,
    level,
    accent,
}: {
    label: string
    level: number
    accent: string
}) {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 3,
                    height: 32,
                }}
            >
                {[0, 1, 2, 3].map((i) => {
                    const threshold = (i + 1) / 5
                    const lit = level >= threshold
                    const fillRatio = Math.max(
                        0.15,
                        Math.min(1, (level - i / 5) * 5)
                    )
                    return (
                        <div
                            key={i}
                            style={{
                                width: 5,
                                height: 32,
                                background: "rgba(255,255,255,0.08)",
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "flex-end",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    width: "100%",
                                    height: `${fillRatio * 100}%`,
                                    background: lit
                                        ? i >= 3
                                            ? "#FF8C5A"
                                            : i >= 2
                                              ? "#FFD27A"
                                              : accent
                                        : `${accent}44`,
                                    boxShadow: lit
                                        ? `0 0 8px ${accent}88`
                                        : "none",
                                    transition: "height 60ms linear",
                                    borderRadius: 2,
                                }}
                            />
                        </div>
                    )
                })}
            </div>
            <span
                style={{
                    fontSize: 8,
                    letterSpacing: "0.22em",
                    color: "rgba(255,255,255,0.55)",
                    textShadow: "0 0 6px rgba(0,0,0,0.8)",
                }}
            >
                {label}
            </span>
        </div>
    )
}
